/**
 * Admin Hub Recents Component
 * Displays recently visited admin pages
 */
import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  NavigateNext as NavIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAdminHub } from './context/AdminHubContext';
import { useAdminHubRecents } from '../../hooks/useAdminHubRecents';
import { ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

// Format relative time
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString();
};

export const AdminHubRecents: React.FC = () => {
  const { navigateTo } = useAdminHub();
  const { recentItems, clearRecents } = useAdminHubRecents();

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="h6" fontWeight={500}>
            Recent Items
          </Typography>
        </Box>
        {recentItems.length > 0 && (
          <Tooltip title="Clear all">
            <IconButton
              size="small"
              onClick={clearRecents}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Recents list */}
      {recentItems.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <HistoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2" gutterBottom>
            No recent items
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pages you visit will appear here for quick access
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {recentItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => navigateTo(item.path)}
                sx={{
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <NavIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {item.section}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ ml: 1, flexShrink: 0 }}
                      >
                        {formatRelativeTime(item.timestamp)}
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    component: 'div',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default AdminHubRecents;
